import React from "react";
import { Navigate } from "react-router-dom";
import { usePermissions, useRole, usePermission } from "../../providers/PermissionsProvider";
import { PageLoader } from "../../components/page-loader";
import { PageLayout } from "../../components/page-layout";
import { EmptyState } from "../../components/onyx/EmptyState";
import { Button } from "../../components/onyx/Button";
import { DashboardPage as MemberDashboard } from "../dashboard-page";
import { AdminDashboard } from "./AdminDashboard";

/**
 * DashboardRouter - Routes to appropriate dashboard based on user type
 *
 * - Members (study participants) → MemberDashboard (onboarding flow)
 * - Customers (B2B researchers) → Redirect to /chat (their primary tool)
 * - Admins (platform admins) → AdminDashboard (system management)
 *
 * SECURITY: Handles error state to prevent unauthorized access when permissions fail to load
 */
export const DashboardRouter = () => {
  const { userType, loading, error } = usePermissions();
  const { isAdmin, isCustomer, isMember } = useRole();
  const { can } = usePermission();

  if (loading) {
    return (
      <PageLayout>
        <PageLoader />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <div className="content-layout">
          <EmptyState
            centered
            title="Unable to Load Permissions"
            description="We couldn't load your account permissions. Please try again."
            detail={`Error: ${error}`}
            action={(
              <Button type="button" variant="primary" onClick={() => window.location.reload()}>
                Retry
              </Button>
            )}
          />
        </div>
      </PageLayout>
    );
  }

  if (!userType) {
    return (
      <PageLayout>
        <div className="content-layout">
          <EmptyState
            centered
            title="Access Denied"
            description="Your account doesn't have permission to access the dashboard."
            detail="Please contact your administrator for access."
          />
        </div>
      </PageLayout>
    );
  }

  if (isAdmin) {
    return <AdminDashboard />;
  }

  if (isCustomer) {
    if (!can('chat:access')) {
      if (can('clinic:phleb_protocol_run')) {
        return <Navigate to="/clinic/phleb" replace />;
      }
      if (can('sample_collection:provider_read')) {
        return <Navigate to="/phleb/collections" replace />;
      }
      return (
        <PageLayout>
          <div className="content-layout">
            <EmptyState
              centered
              title="Access Denied"
              description="Your account doesn't have permission to access the dashboard."
              detail="Please contact your administrator for access."
            />
          </div>
        </PageLayout>
      );
    }
    return <Navigate to="/chat" replace />;
  }

  if (isMember) {
    return <MemberDashboard />;
  }

  return (
    <PageLayout>
      <div className="content-layout">
        <EmptyState
          centered
          title="Unknown User Type"
          description={`Your account has an unrecognized user type: "${userType}"`}
        />
      </div>
    </PageLayout>
  );
};
